/**
 * The core of the What's New module: one state load, one context, and nothing about the app
 * it runs in.
 *
 * ## What the host app hands in, and why the list is this short
 *
 * Three things — `getToken`, `apiBase`, `entries` — plus one optional `onEvent`. That is the
 * whole seam (plan §4, D6). The core knows no tabs, no customers, no roles and no customer
 * name; it knows identifiers the app itself invented, and it hands back a registration
 * interface for markers. A second app copies this folder, replaces `entries.ts`, and is done.
 *
 * `onEvent` is a genuine fourth argument and is named as one rather than smuggled in. The
 * module may not import this app's `services/usage.ts` — that import is exactly what would
 * break on the copy — and telemetry is a requirement of the phase: without it nobody knows in
 * six months whether any of this was worth building. So it is a callback, it is OPTIONAL, and
 * a host that passes only the three required props gets a fully working module with no
 * telemetry. The deviation is written up in handoff-W2.
 *
 * ## D7 of the design-update plan — the host owns the palette (DU6, 2026-09-28)
 *
 * There is now a fifth prop, `tones`, and it is optional at every level. The module used to
 * spell its own Tailwind classes, and a Tailwind class name is not portable: `blue-*` is the
 * brand green in this app and a real blue in `pure-app`, which also has no `info` scale — so
 * one hard-coded map rendered the "Improved" chip colourless over there and the informational
 * notice in "won" green over here. Both were correct against their own comments and wrong on
 * the screen, and nothing inside this folder could have told. `tones.ts` holds the defaults,
 * which hold in a stock Tailwind app, and the reasoning.
 *
 * ## D9 — the failure direction, decided once and held here
 *
 * If the state call fails for any reason (no token, no network, a 500, a body that is not the
 * shape this module understands), the module falls back to "everything seen": no dot, no
 * dialog, no markers, one line in the console. Never "everything new". A database outage that
 * shows nineteen people every entry of the last two years — one of them as a blocking dialog,
 * in the minute before a customer call — is worse than the outage itself. Root `CLAUDE.md`,
 * 2026-05-12: the swallowed error was the real problem there, which is why this one is logged
 * loudly and why the API sends `allSeen` rather than staying quiet about it.
 *
 * ## D8 — an entry dated on or before `seenBeforeDate` counts as seen
 *
 * The API deliberately does not know the entry catalogue (D6), so it answers with a date and
 * the comparison happens here. Without the two `date > seenBeforeDate` lines below, a new
 * colleague opens the app and finds the complete history waiting, which is the first
 * impression D8 exists to prevent. `api/src/functions/whats-new/seen-ids.ts` carries the full
 * argument.
 *
 * **Anchors inherit their entry's date.** They have no date of their own — the W1 handoff says
 * so and concludes that only `allSeen` covers them. That is right for the failure case and not
 * enough for D8: a marker keyed on "not yet dismissed" alone would light up every anchored
 * element in the app for somebody joining next year. So a marker is active when its OWN entry
 * is still inside the D8 window and the anchor has not been used. Being seen in the panel does
 * NOT dismiss a marker: that is the point of the two id namespaces, and of "the marker goes
 * when the element is USED, not when it is seen".
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Notice, WhatsNewEntry, WhatsNewState } from './types';
import { DEFAULT_TONES, resolveTones, type DeepPartialTones, type WhatsNewTones } from './tones';

/** The four moments worth counting. Where they land is the host app's business. */
export type WhatsNewEventName = 'panel-opened' | 'dialog-shown' | 'dialog-closed' | 'show-me';

export interface WhatsNewProviderProps {
    /** A valid access token for `apiBase`. Called per request; may reject. */
    getToken: () => Promise<string>;
    /** The API root, without a trailing slash, e.g. `https://…azurewebsites.net/api`. */
    apiBase: string;
    /** The host app's own `entries.ts`. The one file a second app replaces. */
    entries: readonly WhatsNewEntry[];
    /** Optional, and the only argument beyond the three of D6. See the header. */
    onEvent?: (name: WhatsNewEventName, meta?: Record<string, unknown>) => void;
    /**
     * The host's own classes for the entry chips, the notice levels and the overlay shadow
     * (D7). Optional at every level: anything left out keeps `DEFAULT_TONES`, which holds in a
     * stock Tailwind app. `tones.ts` says why the module cannot decide these for itself.
     */
    tones?: DeepPartialTones;
    children: ReactNode;
}

interface WhatsNewContextValue {
    /** Every entry, newest first. The panel's history section. */
    entries: readonly WhatsNewEntry[];
    /** Unseen and still inside the D8 window. Empty until the state has loaded, and after a failure. */
    unseenEntries: readonly WhatsNewEntry[];
    /** Unseen entries marked `major` — the dialog's content, and nothing else's. */
    majorEntries: readonly WhatsNewEntry[];
    /** False until the state call has settled. Nothing may render a dot before this is true. */
    ready: boolean;
    panelOpen: boolean;
    /** What the panel's "New" section shows: frozen at open, so it cannot empty itself under the reader. */
    panelNewEntries: readonly WhatsNewEntry[];
    openPanel: () => void;
    closePanel: () => void;
    markEntriesSeen: (ids: readonly string[]) => void;
    isAnchorActive: (anchorId: string) => boolean;
    markAnchorUsed: (anchorId: string) => void;
    registerAnchor: (anchorId: string, reveal?: () => void) => () => void;
    /** True while "Show me" is highlighting this anchor. */
    isSpotlit: (anchorId: string) => boolean;
    /** Whether a mounted element claims this anchor AND told us how to reveal it. "Show me" hangs on this. */
    canRevealAnchor: (anchorId: string) => boolean;
    showMe: (entry: WhatsNewEntry) => void;
    loadImage: (imageId: string) => Promise<string | null>;
    releaseImages: () => void;
    /** Active written notices (D5). W5 renders them; the state endpoint already carries them. */
    notices: readonly Notice[];
    /** The host's classes, defaults filled in. Never partial by the time a component reads it. */
    tones: WhatsNewTones;
    track: (name: WhatsNewEventName, meta?: Record<string, unknown>) => void;
}

const WhatsNewContext = createContext<WhatsNewContextValue | null>(null);

/**
 * The context, or a dead but harmless stand-in.
 *
 * A component outside the provider gets "nothing is new" rather than a thrown error: this
 * module may never be the reason a screen fails to render, and an app that forgets the
 * provider around one subtree should lose its markers, not its page.
 */
export function useWhatsNew(): WhatsNewContextValue {
    return useContext(WhatsNewContext) ?? INERT;
}

/** How long "Show me" keeps the element highlighted. Long enough to find, short enough not to nag. */
const SPOTLIGHT_MS = 2600;

/**
 * How often an open tab re-reads the notices — see the refresh effect for why it exists at all.
 *
 * Two minutes is the answer to "how late may somebody hear that the app goes down in twenty".
 * The cost is one small query per open tab per two minutes: at the hundred concurrent users
 * this app is designed against (CLAUDE.md) that is under one request a second, and the
 * freshness half of the answer is cached for 15 s on the server anyway.
 */
const NOTICE_REFRESH_MS = 120_000;

/**
 * The response header saying whether the notices are the central app's answer (`live`) or what
 * the API host could offer without it (`fallback`) — `NOTICES_STATUS_HEADER` in the API half's
 * `notice-source.ts`. Spelled twice because the halves share only `types.ts`, and the wire
 * contract does not change for this (R1).
 */
const NOTICES_STATUS_HEADER = 'X-Whats-New-Notices';

/**
 * The notices to show after a refresh.
 *
 * On `fallback`, the server does not know — so what is on screen stays, and anything the
 * fallback does know is added. The notices' own timers still take each one down when its window
 * closes. Measured on the published host (M3 of the central-notices plan, 2026-09-24): without
 * this, a refresh that met an API worker restarted during an outage of the central app received
 * a calm empty list and took a valid notice off an open tab. Any other value — `live`, or no
 * header at all from an older API — replaces the list, which is what ending a notice early needs.
 */
function mergeNotices(shown: readonly Notice[], incoming: Notice[], status: string | null): Notice[] {
    if (status !== 'fallback') return incoming;
    const ids = new Set(incoming.map(n => n.id));
    return [...incoming, ...shown.filter(n => !ids.has(n.id))];
}

/** `YYYY-MM-DD` in UTC — the same shape and the same clock the API compares against. */
function today(): string {
    return new Date().toISOString().slice(0, 10);
}

/** D9 in one value: everything seen, nothing to show. */
function failedState(): WhatsNewState {
    return { seenEntryIds: [], dismissedAnchors: [], seenBeforeDate: today(), notices: [], allSeen: true };
}

/**
 * Accept the body only if it really is the state.
 *
 * A stale bundle, a proxy error page or a changed envelope would otherwise reach the derivation
 * as `undefined` and read as "nothing seen" — the one direction D9 forbids. Anything that is
 * not the agreed shape is a failure, and a failure means everything seen.
 */
function isState(value: unknown): value is WhatsNewState {
    const s = value as WhatsNewState | null;
    return !!s
        && Array.isArray(s.seenEntryIds)
        && Array.isArray(s.dismissedAnchors)
        && Array.isArray(s.notices)
        && typeof s.seenBeforeDate === 'string'
        && typeof s.allSeen === 'boolean';
}

export function WhatsNewProvider({ getToken, apiBase, entries, onEvent, tones, children }: WhatsNewProviderProps) {
    // Resolved once per distinct object identity. A host passing an inline literal would
    // otherwise hand every render a new `tones` and re-render every consumer of the context;
    // the app's own tones are a module-level constant for the same reason `onEvent` is.
    const resolvedTones = useMemo(() => resolveTones(tones), [tones]);
    const [state, setState] = useState<WhatsNewState | null>(null);
    const [panelOpen, setPanelOpen] = useState(false);
    const [panelNewEntries, setPanelNewEntries] = useState<readonly WhatsNewEntry[]>([]);
    const [spotlight, setSpotlight] = useState<string | null>(null);

    /** Mounted anchors and how to reveal them. A ref, because most readers only need the answer on demand… */
    const anchors = useRef(new Map<string, (() => void) | undefined>());
    /** …and a counter, because the panel must re-render when an anchor mounts after it did. */
    const [anchorVersion, setAnchorVersion] = useState(0);

    /** Blob URLs held while the panel is open, revoked when it closes. */
    const imageUrls = useRef(new Map<string, string>());
    const spotlightTimer = useRef<number | null>(null);

    const track = useCallback(
        (name: WhatsNewEventName, meta?: Record<string, unknown>) => {
            // Telemetry may never be the reason a click does nothing.
            try {
                onEvent?.(name, meta);
            } catch {
                /* deliberately ignored */
            }
        },
        [onEvent],
    );

    /** One call, one round trip, and every failure lands in the same place (D9). */
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const token = await getToken();
                const res = await fetch(`${apiBase}/whats-new/state`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const body = (await res.json()) as { success?: boolean; data?: unknown };
                if (!body?.success || !isState(body.data)) throw new Error('unexpected body');
                if (!cancelled) setState(body.data);
            } catch (err) {
                // Loud in the console, silent on the screen. Both halves are deliberate.
                console.warn('[whats-new] state unavailable — showing nothing rather than everything:', err);
                if (!cancelled) setState(failedState());
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [getToken, apiBase]);

    /**
     * **Notices, and only notices, are re-read while the app is open** (W5).
     *
     * The state call above runs once, which is right for history: an entry goes live with a
     * deploy, and a deploy means a new bundle, which means a reload. A notice is the opposite.
     * It is posted at 18:40 to say the app is down at 19:00, and this audience opens Customer
     * 360 before a call and leaves it open — so a notice that only reaches whoever reloads
     * reaches precisely the people who did not need telling. `--expire` has the same problem
     * from the other side: ending a notice that is wrong has to clear it from the screens it is
     * on, or the only way to take it back is to ask nineteen people to press F5.
     *
     * Only `notices` is taken from the refreshed payload. The seen-state is the one thing this
     * module updates optimistically, and overwriting it with a server read that may be a second
     * behind an in-flight POST would make a dismissed marker flash back onto the screen.
     *
     * Two triggers, both cheap: a timer, and coming back to the tab. A failed refresh keeps
     * what is on screen rather than clearing it — the notice that is up was true a moment ago,
     * and a network blip is not a reason to un-say it. **The same holds for a refresh the API
     * marks as `fallback`** (`mergeNotices`): a 200 is not the same as an answer.
     */
    useEffect(() => {
        if (!state) return;
        let cancelled = false;

        const refresh = async () => {
            try {
                const token = await getToken();
                const res = await fetch(`${apiBase}/whats-new/state`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const body = (await res.json()) as { success?: boolean; data?: unknown };
                if (!body?.success || !isState(body.data)) throw new Error('unexpected body');
                const incoming = body.data.notices;
                const status = res.headers.get(NOTICES_STATUS_HEADER);
                if (!cancelled) {
                    setState(prev => (prev ? { ...prev, notices: mergeNotices(prev.notices, incoming, status) } : prev));
                }
            } catch (err) {
                console.warn('[whats-new] could not refresh the notices; keeping what is shown:', err);
            }
        };

        const timer = window.setInterval(() => void refresh(), NOTICE_REFRESH_MS);
        const onFocus = () => void refresh();
        window.addEventListener('focus', onFocus);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
            window.removeEventListener('focus', onFocus);
        };
        // `state` is in the list only as "has the first call settled"; the refresh reads `prev`.
    }, [state !== null, getToken, apiBase]);

    useEffect(
        () => () => {
            if (spotlightTimer.current !== null) window.clearTimeout(spotlightTimer.current);
            for (const url of imageUrls.current.values()) URL.revokeObjectURL(url);
            imageUrls.current.clear();
        },
        [],
    );

    /** Newest first, and stable: two entries of the same date sort by id, so the panel never reshuffles. */
    const sorted = useMemo(
        () => [...entries].sort((a, b) => (a.date === b.date ? b.id.localeCompare(a.id) : b.date.localeCompare(a.date))),
        [entries],
    );

    const live = state && !state.allSeen ? state : null;

    const unseenEntries = useMemo(() => {
        if (!live) return [] as WhatsNewEntry[];
        const seen = new Set(live.seenEntryIds);
        // D8: dated on or before the baseline counts as seen, without the server knowing an entry.
        return sorted.filter(e => !seen.has(e.id) && e.date > live.seenBeforeDate);
    }, [live, sorted]);

    const majorEntries = useMemo(() => unseenEntries.filter(e => e.major), [unseenEntries]);

    const activeAnchors = useMemo(() => {
        if (!live) return new Set<string>();
        const dismissed = new Set(live.dismissedAnchors);
        const open = new Set<string>();
        for (const e of sorted) {
            // The anchor's date IS its entry's date — see the header. `seenEntryIds` is
            // deliberately not consulted: a marker goes when the element is used.
            if (e.anchor && e.date > live.seenBeforeDate && !dismissed.has(e.anchor)) open.add(e.anchor);
        }
        return open;
    }, [live, sorted]);

    /**
     * Post an acknowledgement, and put it back if the post failed.
     *
     * The optimistic update is what makes a marker disappear under the finger that used it. The
     * rollback is W1's instruction: a failed POST is ignored, so the marker stands until the
     * next attempt. D9 is the rule never to INVENT news — it is not a licence to claim a write
     * that did not happen.
     */
    const post = useCallback(
        async (payload: { entryIds?: string[]; anchors?: string[] }, rollback: () => void) => {
            try {
                const token = await getToken();
                const res = await fetch(`${apiBase}/whats-new/seen`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
            } catch (err) {
                console.warn('[whats-new] could not record what was seen — it stays unseen:', err);
                rollback();
            }
        },
        [getToken, apiBase],
    );

    const markEntriesSeen = useCallback(
        (ids: readonly string[]) => {
            if (ids.length === 0 || !state) return;
            const fresh = ids.filter(id => !state.seenEntryIds.includes(id));
            if (fresh.length === 0) return;
            setState(prev => (prev ? { ...prev, seenEntryIds: [...prev.seenEntryIds, ...fresh] } : prev));
            void post({ entryIds: fresh }, () =>
                setState(prev =>
                    prev ? { ...prev, seenEntryIds: prev.seenEntryIds.filter(id => !fresh.includes(id)) } : prev,
                ),
            );
        },
        [state, post],
    );

    const markAnchorUsed = useCallback(
        (anchorId: string) => {
            if (!live || live.dismissedAnchors.includes(anchorId)) return;
            setState(prev => (prev ? { ...prev, dismissedAnchors: [...prev.dismissedAnchors, anchorId] } : prev));
            void post({ anchors: [anchorId] }, () =>
                setState(prev =>
                    prev ? { ...prev, dismissedAnchors: prev.dismissedAnchors.filter(a => a !== anchorId) } : prev,
                ),
            );
        },
        [live, post],
    );

    const registerAnchor = useCallback((anchorId: string, reveal?: () => void) => {
        anchors.current.set(anchorId, reveal);
        setAnchorVersion(v => v + 1);
        return () => {
            anchors.current.delete(anchorId);
            setAnchorVersion(v => v + 1);
        };
    }, []);

    const releaseImages = useCallback(() => {
        for (const url of imageUrls.current.values()) URL.revokeObjectURL(url);
        imageUrls.current.clear();
    }, []);

    const openPanel = useCallback(() => {
        // Frozen at open: the same click marks these entries seen, and a list that emptied
        // itself while being read would be the one thing worse than not showing it at all.
        setPanelNewEntries(unseenEntries);
        setPanelOpen(true);
        track('panel-opened', { newCount: unseenEntries.length });
        markEntriesSeen(unseenEntries.map(e => e.id));
    }, [unseenEntries, track, markEntriesSeen]);

    const closePanel = useCallback(() => {
        setPanelOpen(false);
        releaseImages();
    }, [releaseImages]);

    const showMe = useCallback(
        (entry: WhatsNewEntry) => {
            track('show-me', { entryId: entry.id, anchor: entry.anchor });
            closePanel();
            const anchorId = entry.anchor;
            if (!anchorId) return;
            // The app knows how to get there; the module only knows that it asked.
            anchors.current.get(anchorId)?.();
            setSpotlight(anchorId);
            if (spotlightTimer.current !== null) window.clearTimeout(spotlightTimer.current);
            spotlightTimer.current = window.setTimeout(() => setSpotlight(null), SPOTLIGHT_MS);
        },
        [track, closePanel],
    );

    /**
     * An image, as a blob URL.
     *
     * `<img src>` cannot carry an `Authorization` header, and the image sits behind the token
     * because the bundle is served anonymously and a screenshot carries a customer name, a
     * turnover and a margin (D4, W0 measurement 5). So: fetch with the token,
     * `createObjectURL`, and revoke when the panel closes. The CSP needs `blob:` in `img-src`
     * for the last step — `web/public/staticwebapp.config.json` says why.
     */
    const loadImage = useCallback(
        async (imageId: string): Promise<string | null> => {
            const cached = imageUrls.current.get(imageId);
            if (cached) return cached;
            try {
                const token = await getToken();
                const res = await fetch(`${apiBase}/whats-new/image/${encodeURIComponent(imageId)}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const url = URL.createObjectURL(await res.blob());
                // A second caller may have won the race while this one awaited.
                const raced = imageUrls.current.get(imageId);
                if (raced) {
                    URL.revokeObjectURL(url);
                    return raced;
                }
                imageUrls.current.set(imageId, url);
                return url;
            } catch (err) {
                console.warn(`[whats-new] image "${imageId}" unavailable:`, err);
                return null;
            }
        },
        [getToken, apiBase],
    );

    /**
     * Which anchors can be revealed right now: mounted AND registered with a `reveal`.
     *
     * A snapshot rather than a read of the ref, so that an anchor mounting after the panel did
     * re-renders the panel — an offer that arrived a render too late would simply never appear.
     * Anchors without a `reveal` are excluded, because "Show me" would then close the panel and
     * leave the reader wherever they were, which is worse than not offering it.
     */
    const revealable = useMemo(
        () => new Set([...anchors.current.entries()].filter(([, reveal]) => !!reveal).map(([id]) => id)),
        [anchorVersion],
    );

    const value = useMemo<WhatsNewContextValue>(
        () => ({
            entries: sorted,
            unseenEntries,
            majorEntries,
            ready: state !== null,
            panelOpen,
            panelNewEntries,
            openPanel,
            closePanel,
            markEntriesSeen,
            isAnchorActive: (anchorId: string) => activeAnchors.has(anchorId),
            markAnchorUsed,
            registerAnchor,
            isSpotlit: (anchorId: string) => spotlight === anchorId,
            canRevealAnchor: (anchorId: string) => revealable.has(anchorId),
            showMe,
            loadImage,
            releaseImages,
            // `state`, not `live`: D9 is about what this person has SEEN, and a notice is not
            // seen-state. The API says the same thing from the other side (`get-state.ts`) —
            // it appends the calculated notice outside the state read, so a degraded payload
            // can still carry one. "The HubSpot data is overdue" is exactly what somebody
            // wants on a morning when something else is broken, and suppressing it would make
            // the fail-safe hide the outage instead of surviving it.
            notices: state?.notices ?? [],
            tones: resolvedTones,
            track,
        }),
        [
            sorted, unseenEntries, majorEntries, state, panelOpen, panelNewEntries, openPanel, closePanel,
            markEntriesSeen, activeAnchors, markAnchorUsed, registerAnchor, spotlight, revealable,
            showMe, loadImage, releaseImages, live, track, resolvedTones,
        ],
    );

    return <WhatsNewContext.Provider value={value}>{children}</WhatsNewContext.Provider>;
}

/** What `useWhatsNew()` answers outside a provider: a module that shows nothing and breaks nothing. */
const INERT: WhatsNewContextValue = {
    entries: [],
    unseenEntries: [],
    majorEntries: [],
    ready: false,
    panelOpen: false,
    panelNewEntries: [],
    openPanel: () => {},
    closePanel: () => {},
    markEntriesSeen: () => {},
    isAnchorActive: () => false,
    markAnchorUsed: () => {},
    registerAnchor: () => () => {},
    isSpotlit: () => false,
    canRevealAnchor: () => false,
    showMe: () => {},
    loadImage: async () => null,
    releaseImages: () => {},
    notices: [],
    tones: DEFAULT_TONES,
    track: () => {},
};
